import type React from "react";
import type {ContractDetail, ContractDocument, ContractVersion} from "../contractExplorerTypes.ts";
import {formatDuration, formatValue, termOf} from "../contractExplorerFormat.ts";
import {Section} from "./Section.tsx";
import type {Term} from "./TermList.tsx";
import {TermList} from "./TermList.tsx";

type Props = {
    contract: ContractDetail;
    version: ContractVersion | undefined;
    documents: Record<string, ContractDocument>;
};

const TERMS: Term[] = [
    {field: 'renewal_mechanism', label: 'Renewal'},
    {field: 'initial_term', label: 'Initial term', format: formatDuration},
    {field: 'renewal_term', label: 'Renewal term', format: formatDuration},
    {field: 'renewal_notice_period', label: 'Renewal notice', format: formatDuration},
    {field: 'convenience_termination_by', label: 'Termination for convenience by'},
    {field: 'termination_notice_period', label: 'Termination notice', format: formatDuration},
    {field: 'price_indexation', label: 'Price indexation'},
    {field: 'fee_terms', label: 'Fees'},
];

const PAYMENT: Term[] = [
    {field: 'payment_net_days', label: 'Net days', format: v => (typeof v === 'number' ? `Net ${v}` : formatValue(v))},
    {field: 'payment_trigger', label: 'Counted from'},
    {field: 'payment_trigger_details', label: 'Trigger details'},
    {field: 'payment_method', label: 'Payment method'},
    {field: 'early_payment_discount_pct', label: 'Early-payment discount', format: v => `${formatValue(v)}%`},
    {field: 'early_payment_discount_days', label: 'Discount within', format: v => `${formatValue(v)} days`},
];

/** The bundle's terms and payment terms in the shown version, each cited and marked when it changed. */
export const TermsSection: React.FC<Props> = props => {
    const stated = TERMS.filter(t => termOf(props.contract, props.version, t.field)).length;
    const paid = PAYMENT.filter(t => termOf(props.contract, props.version, t.field)).length;
    return <>
        <Section title="Terms"
                 sub={stated ? undefined : 'No document states a renewal, termination, indexation or fee term.'}>
            <TermList {...props} terms={TERMS}/>
        </Section>
        <Section title="Payment terms" sub={paid ? undefined : 'No document states a payment term.'}>
            <TermList {...props} terms={PAYMENT}/>
        </Section>
    </>;
};
